import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  IconButton,
  Tooltip
} from '@mui/material';
import { FormatBold, FormatClear } from '@mui/icons-material';
import { db, auth } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import FormattedTextEditor from './FormattedTextEditor';
import { getPlainText } from '../utils/textFormatting';

const EditHighlight = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [highlight, setHighlight] = useState(null);
  const [text, setText] = useState('');
  const [formattedText, setFormattedText] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [hasSelection, setHasSelection] = useState(false);
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const textFieldRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // 모바일 키보드 높이 감지 (visualViewport API - 하단 액션 바 위치용, create와 동일)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.visualViewport) return;
    const handleViewportResize = () => {
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const wh = window.innerHeight;
      setKeyboardHeight(Math.max(0, wh - vh));
    };
    handleViewportResize();
    window.visualViewport.addEventListener('resize', handleViewportResize);
    window.visualViewport.addEventListener('scroll', handleViewportResize);
    return () => {
      window.visualViewport.removeEventListener('resize', handleViewportResize);
      window.visualViewport.removeEventListener('scroll', handleViewportResize);
    };
  }, []);

  useEffect(() => {
    const fetchHighlight = async () => {
      try {
        const highlightDoc = await getDoc(doc(db, 'highlights', id));
        if (!highlightDoc.exists()) {
          navigate('/');
          return;
        }
        const highlightData = highlightDoc.data();
        if (highlightData.uid !== auth.currentUser?.uid) {
          navigate('/');
          return;
        }
        setHighlight(highlightData);
        setText(highlightData.text);
        
        // formattedText가 있으면 사용, 없으면 일반 텍스트로 변환
        const initialFormattedText = highlightData.formattedText || {
          text: highlightData.text || '',
          formatting: []
        };
        setFormattedText(initialFormattedText);
      } catch (error) {
        console.error('하이라이트 불러오기 오류:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };
    fetchHighlight();
  }, [id, navigate]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // formattedText에서 plain text 추출
    const currentText = formattedText ? getPlainText(formattedText) : text;
    
    if (!currentText.trim() || currentText.trim() === highlight.text) return;
    setSaving(true);
    try {
      const updateData = {
        text: currentText.trim(),
        updatedAt: serverTimestamp()
      };
      
      // formattedText가 있으면 저장
      if (formattedText) {
        updateData.formattedText = formattedText;
      }
      
      await updateDoc(doc(db, 'highlights', id), updateData);
      setSnackbar({ open: true, message: '하이라이트가 수정되었습니다.', severity: 'success' });
      setTimeout(() => {
        const bookDocId = `${highlight.uid}_${highlight.bookId}`;
        navigate(`/book/${bookDocId}?uid=${highlight.uid}`);
      }, 500);
    } catch (error) {
      console.error('하이라이트 수정 오류:', error);
      setSnackbar({ open: true, message: '하이라이트 수정에 실패했습니다.', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!highlight) {
    return null;
  }

  const ACTION_BAR_HEIGHT = 56;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', bgcolor: '#fff' }}>
      {/* 메인 콘텐츠 영역 - create와 100% 동일한 구조/스타일 */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          overflow: 'hidden',
          bgcolor: '#ffffff',
          pt: 'env(safe-area-inset-top, 0px)',
          pb: `calc(${ACTION_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
          position: 'relative',
        }}
      >
        {/* 메인 입력란 - create와 동일한 wrapper (minHeight: 0, overflow 등) */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <FormattedTextEditor
            ref={textFieldRef}
            value={formattedText}
            onChange={setFormattedText}
            placeholder="문장 내용을 입력하세요..."
            minRows={15}
            maxRows={40}
            disabled={saving}
            onSelectionChange={setHasSelection}
            disableFormatMenu={true}
          />
        </Box>
      </Box>

      {/* 하단 고정 플로팅 액션 바 - create와 동일 구조 (스캔 버튼 없음), 전체 컨테이너와 동일한 max-width */}
      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: keyboardHeight,
          zIndex: 1200,
          maxWidth: '600px',
          margin: '0 auto',
          width: '100%',
          px: 2,
          py: 1,
          pb: 'calc(8px + env(safe-area-inset-bottom, 0px))',
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: 44,
          gap: 1,
        }}
      >
        {/* 좌측: 서식 관련 (B, 포맷 해제) - 스캔 버튼 없음 */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title={hasSelection ? "볼드 적용" : "텍스트 선택 후 볼드"}>
            <IconButton
              onPointerDown={(e) => {
                e.preventDefault();
                if (saving) return;
                if (textFieldRef.current?.applyBold) {
                  hasSelection ? textFieldRef.current.applyBold() : setSnackbar({ open: true, message: '볼드로 변경할 텍스트를 먼저 선택해주세요.', severity: 'info' });
                }
              }}
              disabled={saving}
              sx={{ minWidth: 44, minHeight: 44, color: hasSelection ? '#111' : '#999', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
            >
              <FormatBold sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={hasSelection ? "포맷 해제" : "텍스트 선택 후 포맷 해제"}>
            <IconButton
              onPointerDown={(e) => {
                e.preventDefault();
                if (saving) return;
                if (textFieldRef.current?.removeBold) {
                  hasSelection ? textFieldRef.current.removeBold() : setSnackbar({ open: true, message: '볼드 포맷팅을 제거할 텍스트를 먼저 선택해주세요.', severity: 'info' });
                }
              }}
              disabled={saving}
              sx={{ minWidth: 44, minHeight: 44, color: hasSelection ? '#111' : '#999', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
            >
              <FormatClear sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
        </Box>
        {/* 우측: 흐름 제어 (취소, 저장) */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button
            onClick={handleGoBack}
            disabled={saving}
            sx={{ minHeight: 40, minWidth: 44, px: 1.5, fontSize: '0.875rem', fontWeight: 500, color: '#666', textTransform: 'none', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={saving}
            sx={{
              minHeight: 40,
              minWidth: 44,
              px: 1.5,
              fontSize: '0.875rem',
              fontWeight: 600,
              bgcolor: '#111',
              color: '#fff',
              borderRadius: '8px',
              boxShadow: 'none',
              textTransform: 'none',
              '&:hover': { bgcolor: '#222', boxShadow: 'none' },
            }}
          >
            {saving ? <CircularProgress size={22} color="inherit" /> : '저장'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={snackbar.autoHideDuration || 3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EditHighlight;